import { z } from 'zod';
import { AuditSchema, DateOnlySchema, IdSchema } from './common.ts';
import { addMoney, money, type Money } from '../money.ts';
import { CostLineSchema, costTotals, lineBuilderCost } from './cost-line.ts';

/**
 * A receipt in the cost inbox: something somebody bought, with the slip.
 *
 * `docs/features/08-purchase-orders-bills.md` §4. The distinction from a
 * bill is who is waiting: a **bill** is an invoice somebody has sent and is
 * owed, a **receipt** is a purchase already paid for that has to be
 * attributed to a job. They end up in the same place — `actual` on the
 * budget — by two different routes, and a receipt can be **converted** into
 * a bill when it turns out to need the bill's machinery (a lien waiver, a
 * payment history, a recharge to the owner).
 *
 * **There is no `ocr` field.** The data model reserved one, and
 * `docs/features/08` §4 has line extraction behind an `OcrPort` that is off
 * in v1. Storing an empty object for it would be a field that means
 * "nobody has looked" and reads as "nothing was found"; the port arrives
 * with a provider, and the field arrives with the port.
 */
export const RECEIPT_STATUSES = ['new', 'reviewed', 'converted', 'rejected'] as const;
export const ReceiptStatusSchema = z.enum(RECEIPT_STATUSES);
export type ReceiptStatus = z.infer<typeof ReceiptStatusSchema>;

export const RECEIPT_STATUS_LABEL: Record<ReceiptStatus, string> = {
  new: 'New',
  reviewed: 'Reviewed',
  converted: 'Converted to a bill',
  rejected: 'Rejected',
};

/**
 * The transitions this product allows.
 *
 * `rejected → new` is deliberate: a receipt rejected as somebody else's is
 * the kind of thing that turns out to be yours after all, and the way back
 * must not be to photograph the slip again. Nothing leaves `converted`,
 * because the bill is the record from then on — un-converting would leave
 * a bill nobody can trace.
 *
 * `converted` is reached by the callable only, which is why it is not in
 * any list here: `convertReceiptToBill` writes the bill and the status in
 * one transaction, and a client that could write `converted` could mark a
 * receipt done without a bill existing.
 */
export const RECEIPT_TRANSITIONS: Readonly<Record<ReceiptStatus, readonly ReceiptStatus[]>> = {
  new: ['reviewed', 'rejected'],
  reviewed: ['new', 'rejected'],
  rejected: ['new'],
  converted: [],
};

export function canTransitionReceipt(from: ReceiptStatus, to: ReceiptStatus): boolean {
  return RECEIPT_TRANSITIONS[from].includes(to);
}

/** Editable until it becomes a bill. A rejected one is still editable — it may come back. */
export function isReceiptEditable(status: ReceiptStatus): boolean {
  return status !== 'converted';
}

export const ReceiptSchema = AuditSchema.extend({
  id: IdSchema,
  companyId: IdSchema,
  jobId: IdSchema,
  title: z.string().trim().min(1).max(200),
  description: z.string().max(20_000).default(''),
  /** When it was bought, which is not when somebody got round to filing it. */
  purchaseDate: DateOnlySchema.optional(),
  vendorContactId: IdSchema.optional(),
  status: ReceiptStatusSchema.default('new'),
  lines: z.array(CostLineSchema).default([]),
  tagIds: z.array(IdSchema).default([]),
  /** Set when it became a bill, which is also when it stops counting on its own. */
  billId: IdSchema.optional(),
  /**
   * Where it came from.
   *
   * `upload` is somebody dragging a photograph in. `email` is the
   * receipts-in address §4 describes and `mobile` is the phone app; both
   * are later, and the field is here so a receipt filed by hand today is
   * distinguishable from one that arrives automatically tomorrow.
   */
  source: z.enum(['upload', 'email', 'mobile']).default('upload'),
});
export type Receipt = z.infer<typeof ReceiptSchema>;

/** What was spent, in money: the cost of its lines. */
export function receiptTotal(receipt: Pick<Receipt, 'lines'>, currency = 'USD'): Money {
  return costTotals(receipt.lines, currency).cost;
}

/**
 * Whether this receipt may become a bill.
 *
 * It needs lines, because a bill of nothing costs the job nothing and the
 * conversion would produce a document somebody has to go back and fill in.
 * And it needs not to be converted already — that one is also enforced in
 * the callable's transaction, because this answer is a convenience and
 * never a control.
 */
export function canConvertReceipt(receipt: Pick<Receipt, 'status' | 'lines'>): boolean {
  return (
    receipt.status !== 'converted' && receipt.status !== 'rejected' && receipt.lines.length > 0
  );
}

/**
 * What receipts contribute to `actual`, by cost code.
 *
 * Two rules, and each is there to stop a wrong number rather than for
 * tidiness:
 *
 * - **A converted receipt counts nothing.** Its bill counts instead, and
 *   counting both would double the cost of everything that went through
 *   the inbox. This is the single most important line in this file.
 * - **A rejected one counts nothing**, because rejecting it is the act of
 *   saying it is not this job's.
 *
 * The accounting method makes no difference, which is the interesting part:
 * a receipt **is** proof of payment. Somebody has already paid for it, so
 * it is actual cost on the cash method as surely as on the accrual one, and
 * taking the method as an argument would invite a caller to think it
 * mattered.
 */
export function receiptActualByCostCode(
  receipts: readonly Receipt[],
  currency = 'USD',
): Map<string | null, Money> {
  const byCode = new Map<string | null, Money>();
  for (const receipt of receipts) {
    if (receipt.status === 'converted' || receipt.status === 'rejected') continue;
    for (const line of receipt.lines) {
      const key = line.costCodeId ?? null;
      byCode.set(key, addMoney(byCode.get(key) ?? money(0, currency), lineBuilderCost(line)));
    }
  }
  return byCode;
}

/** Receipts still waiting on somebody, which is what the inbox is counted by. */
export function receiptsAwaiting(receipts: readonly Receipt[]): Receipt[] {
  return receipts.filter((receipt) => receipt.status === 'new' || receipt.status === 'reviewed');
}
